import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Statement } from "@/components/Statement";
import { StatementCharts } from "@/components/StatementCharts";
import { PdfButton, ReportList, useReportPage } from "@/components/ReportShell";
import { fmtMoney, fmtPct } from "@/lib/api";
import { NoData } from "@/components/NoData";

const FILTERS = {
  individual: (e) => e.kind === "individual",
  trust: (e) => e.kind === "trust",
  all: (e) => e.kind !== "entity",
};

const Holdings = ({ rep }) => (
  <section className="statement mt-6" data-testid="networth-holdings">
    <h3 className="font-serif text-xl text-slate-900 border-b border-slate-300 pb-1">Ownership interests</h3>
    <p className="text-xs text-slate-500 mt-2">Direct % is held in this name; effective % includes everything held through other entities.</p>
    <div className="overflow-x-auto">
      <table className="w-full text-sm mt-4 min-w-[560px]">
        <thead><tr className="small-label text-left border-b"><th className="py-2">Entity</th><th className="text-right">Direct %</th><th className="text-right">Effective %</th><th className="text-right">Direct value</th></tr></thead>
        <tbody>
          {rep.holdings.map((h) => (
            <tr key={h.entity_id} className="border-b border-slate-100">
              <td className="py-1.5"><Link to={`/reports/${h.kind === "entity" ? "entities" : "networth"}/${h.entity_id}`} className="hover:text-amber-700 transition-colors duration-200">{h.name}</Link></td>
              <td className="num text-right">{h.direct_pct ? fmtPct(h.direct_pct) : "—"}</td>
              <td className="num text-right">{fmtPct(h.effective_pct)}</td>
              <td className="num text-right">{h.direct_pct ? fmtMoney(h.direct_value) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

const LookThrough = ({ rep }) => (
  <section className="statement mt-6" data-testid="networth-lookthrough">
    <h3 className="font-serif text-xl text-slate-900 border-b border-slate-300 pb-1">Look-through asset composition</h3>
    <p className="text-xs text-slate-500 mt-2">This holder's share of every underlying asset, after rolling ownership through all entities.</p>
    <div className="mt-3">
      {rep.lookthrough_assets.map((a) => (
        <div key={a.category} className="ledger-row"><span>{a.category}</span><span className="num text-right">{fmtMoney(a.amount)}</span></div>
      ))}
      <div className="ledger-row font-semibold"><span>Total look-through assets</span><span className="num text-right rule-single">{fmtMoney(rep.lookthrough_total_assets, true)}</span></div>
      <div className="ledger-row"><span>Less look-through liabilities</span><span className="num text-right">{fmtMoney(rep.lookthrough_total_liabilities)}</span></div>
      <div className="ledger-row font-semibold mt-2"><span>Net worth</span><span className="num text-right rule-double" data-testid="networth-total">{fmtMoney(rep.net_assets, true)}</span></div>
    </div>
  </section>
);

export default function NetWorth() {
  const { id } = useParams();
  const [filter, setFilter] = useState("all");
  const { api, period, rows, rep } = useReportPage(FILTERS[filter], "/reports/networth", id);
  if (!period) return <NoData />;
  return (
    <div className="max-w-[1400px]">
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">Net Worth Statements</h1>
      <p className="text-slate-500 mt-3">Individual and trust statements with look-through ownership.</p>
      <div className="flex border border-slate-200 bg-white w-fit mt-8 no-print">
        {[["all", "All holders"], ["individual", "Individuals"], ["trust", "Trusts & estates"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`networth-filter-${v}`}
            className={`px-4 py-2 text-sm transition-colors duration-200 ${filter === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      <div className="grid lg:grid-cols-[300px_1fr] gap-8 mt-6">
        <div className="no-print"><ReportList rows={rows} activeId={id} base="/reports/networth" testid="networth-list" /></div>
        <div className="min-w-0">
          {rep ? (
            <>
              <div className="flex justify-end mb-4 no-print">
                <PdfButton api={api} url={`/reports/networth/${rep.entity.id}/pdf?period=${period}`} name="networth.pdf" />
              </div>
              <div className="fade-up"><StatementCharts entityId={rep.entity.id} rows={rep.lookthrough_assets} title="Asset mix (look-through)" period={rep.period} /><Statement rep={rep} title="Statement of Net Worth" /></div>
              {rep.holdings.length > 0 && <Holdings rep={rep} />}
              <LookThrough rep={rep} />
            </>
          ) : (
            <p className="small-label">Loading statement…</p>
          )}
        </div>
      </div>
    </div>
  );
}
